<template>
  <div class="sizes">
    <h3>Letter Height</h3>
    <div class="sizes__height">
      <div
        v-for="height in heights"
        :key="height.value"
        :class="{
          sizes__size: true,
          'sizes__size--selected': isHeightSelected(height)
        }"
        @click="handleHeightSet(height)"
      >
        {{ height.label }}
      </div>

      <div
        :class="{
          sizes__size: true,
          'sizes__size--selected': showCustomHeight
        }"
        @click="handleCustomHeight()"
      >
        Custom
      </div>
    </div>

    <template v-if="showCustomHeight">
      <p class="sizes__label">Height (inches)</p>
      <input
        v-model="letterHeight"
        maxlength="6"
        type="number"
        class="sizes__input"
        @input="handleCustomInputChange($event, updateHeight)"
      />
      <ArtworkAlert v-if="letterHeight && !isValidLetterHeight" class="sizes__error">
        Please enter a valid height between {{ dtfMinHeight }} and {{ dtfMaxHeight }} inches
      </ArtworkAlert>
    </template>

    <h3>Maximum Length</h3>
    <div class="sizes__max-lengths">
      <div
        v-for="maxLength in maxLengths"
        :key="maxLength.value"
        :class="{
          sizes__size: true,
          'sizes__size--selected': isMaxLengthSelected(maxLength)
        }"
        @click="handleMaxLengthSet(maxLength)"
      >
        {{ maxLength.label }}
      </div>

      <div
        :class="{
          sizes__size: true,
          'sizes__size--selected': showCustomMaxLength
        }"
        @click="handleCustomMaxLength()"
      >
        Custom
      </div>
    </div>

    <template v-if="showCustomMaxLength">
      <p class="sizes__label">Length (inches)</p>
      <input
        v-model="letterMaxLength"
        maxlength="6"
        type="number"
        class="sizes__input"
        @input="handleCustomInputChange($event, updateMaxLength)"
      />
      <ArtworkAlert v-if="letterMaxLength && !isValidLetterMaxLength" class="sizes__error">
        Please enter a valid max length between {{ dtfMinWidth }} and {{ dtfMaxWidth }} inches
      </ArtworkAlert>
    </template>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, watch } from "vue";
import ArtworkAlert from "components/jiffy/vue-product-details-dtf/ArtworkAlert.vue";
import { useDtfStore } from "../store";

const heights = [
  { label: "2 Inches", value: 2 },
  { label: "2.5 Inches", value: 2.5 },
  { label: "3 Inches", value: 3 }
];
const heightValues = heights.map(height => height.value);

const maxLengths = [
  { label: "9 Inches", value: 9 },
  { label: "11 Inches", value: 11 },
  { label: "14 Inches", value: 14 }
];
const maxLengthValues = maxLengths.map(maxLength => maxLength.value);

let timer = null;

const letterHeight = ref(null);
const letterMaxLength = ref(null);

const store = useDtfStore();
const letterNumberArtwork = computed(() => store.letterNumberArtwork);
const isHeightSelected = option =>
  letterNumberArtwork.value.height === option.value && !letterNumberArtwork.value.showCustomHeight;
const isMaxLengthSelected = option =>
  letterNumberArtwork.value.maxLength === option.value &&
  !letterNumberArtwork.value.showCustomMaxLength;

const showCustomHeight = computed(() => letterNumberArtwork.value.showCustomHeight);
const showCustomMaxLength = computed(() => letterNumberArtwork.value.showCustomMaxLength);

const dtfMinHeight = computed(() => store.dtfMinHeight);
const dtfMaxHeight = computed(() => store.dtfMaxHeight);
const dtfMinWidth = computed(() => store.dtfMinWidth);
const dtfMaxWidth = computed(() => store.dtfMaxWidth);

const isValidLetterHeight = computed(
  () => letterHeight.value >= dtfMinHeight.value && letterHeight.value <= dtfMaxHeight.value
);

const isValidLetterMaxLength = computed(
  () => letterMaxLength.value >= dtfMinWidth.value && letterMaxLength.value <= dtfMaxWidth.value
);

const isValidDimension = () => isValidLetterMaxLength.value && isValidLetterHeight.value;

const updateHeight = option => {
  let height = option.value;
  const isValid = isValidDimension();

  // This ensures that we only generating previews for our maximum supported height.
  // Allowing values greater than dtfMaxHeight causes issue with performance.
  // Generating images more than we allow doesn't make sense
  if (height > dtfMaxHeight.value) height = dtfMaxHeight.value;

  store.updateArtworkValues({ index: 0, isValid, height });

  if (isValid) store.updateAllLetterPreviews();
};

const updateMaxLength = option => {
  let maxLength = option.value;
  const isValid = isValidDimension();

  // This ensures that we only generating previews for our maximum supported width.
  // Allowing values greater than dtfMaxWidth causes issue with performance.
  // Generating images more than we allow doesn't make sense
  if (maxLength > dtfMaxWidth.value) maxLength = dtfMaxWidth.value;

  store.updateArtworkValues({ index: 0, isValid, maxLength });

  if (isValid) store.updateAllLetterPreviews();
};

const handleHeightSet = option => {
  letterHeight.value = option.value;
  store.updateArtworkValues({ index: 0, showCustomHeight: false });
  updateHeight(option);
};

const handleMaxLengthSet = option => {
  letterMaxLength.value = option.value;
  store.updateArtworkValues({ index: 0, showCustomMaxLength: false });
  updateMaxLength(option);
};

const handleCustomHeight = () => {
  store.updateArtworkValues({
    index: 0,
    isValid: false,
    showCustomHeight: true,
    height: null
  });
};

const handleCustomMaxLength = () => {
  store.updateArtworkValues({
    index: 0,
    isValid: false,
    showCustomMaxLength: true,
    maxLength: null
  });
};

const handleCustomInputChange = (event, callback) => {
  clearTimeout(timer);

  const { value } = event.target;
  let inputValue = value.match(/(\d+(\.\d*)?)/);

  // Any null matching should default to 0
  inputValue = inputValue ? inputValue[0] : "0";

  const splitHeight = inputValue.split(".");

  // Skip formatting to 2 decimal places if last element is '.'
  if (splitHeight[1] && splitHeight[1].length > 2) {
    // Format inputValue to 2 decimal places
    // We can't use `toFixed` since its rounding the digits off
    inputValue = Number([splitHeight[0], splitHeight[1].slice(0, 2)].join("."));
  }

  // Delay setting of inputValue once user stops typing for 1 second
  timer = setTimeout(() => {
    callback(ref(Number(inputValue)));
  }, 1000);
};

watch(
  () => letterNumberArtwork.value.height,
  (newValue, oldValue) => {
    // clearWorkspace was called
    if (oldValue && newValue === null) {
      letterHeight.value = null;
    }
  }
);

watch(
  () => letterNumberArtwork.value.maxLength,
  (newValue, oldValue) => {
    // clearWorkspace was called
    if (oldValue && newValue === null) {
      letterMaxLength.value = null;
    }
  }
);

onMounted(() => {
  const { height, maxLength } = letterNumberArtwork.value;

  if (!height || !maxLength) {
    return;
  }

  const isValid = isValidLetterHeight.value && isValidLetterMaxLength.value;
  let showCustomHeightInput = false;
  let showCustomMaxLengthInput = false;

  if (!heightValues.includes(height)) showCustomHeightInput = true;
  if (!maxLengthValues.includes(maxLength)) showCustomMaxLengthInput = true;

  store.updateArtworkValues({
    index: 0,
    isValid,
    showCustomHeight: showCustomHeightInput,
    showCustomMaxLength: showCustomMaxLengthInput
  });
});
</script>

<style lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.sizes {
  &__height,
  &__max-lengths {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  }

  &__size {
    padding: 12px 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $text-black;
    font-size: 13px;
    font-weight: 700;
    line-height: 1.3;
    cursor: pointer;
    border-radius: 4px;
    border: 2px solid $gray-border;
    background: $white;

    &--selected {
      border-color: $primary-600;
    }
  }

  &__label {
    margin: 24px 0 12px;
    color: $text-black;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.1;
  }

  &__input {
    appearance: none;
    border-radius: 4px;
    border: 1px solid $gray-label;
    background: $white;
    padding: 13px 18px;
    color: $text-black;
    text-align: center;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.42;
  }

  &__input::-webkit-outer-spin-button,
  &__input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &__error {
    margin-top: 8px;
  }
}
</style>
